import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addEmployee,
  deleteUser,
  exportUsersCSV,
  getRoles,
  getUsers,
  updateUser,
  updateUserStatus,
} from "../Auth";
import toast from "react-hot-toast";
import {
  INACTIVE_TO_ACTIVE_REQUIRES_SUPPORT_MESSAGE,
  isEmployeeInactiveStatus,
  normalizeEmployeeStatus,
} from "../constants/employeeStatus";

/** Map UI filter codes to role names stored in the database. */
const POSITION_TO_ROLE_NAME = {
  ADMIN: "Admin",
};

const resolveRoleName = (position) =>
  POSITION_TO_ROLE_NAME[position] || position;

const resolveRoleId = (position, roles) => {
  if (!position || position === "All" || !roles?.length) return null;
  const roleName = resolveRoleName(position);
  const role = roles.find(
    (r) =>
      r.name === roleName ||
      r.name?.toUpperCase() === roleName.toUpperCase(),
  );
  return role?.id ?? null;
};

export const useEmployees = (initialFilters = {}) => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        limit: 20,
        total: 0
    });

    // Filter state
    const [filters, setFilters] = useState({
        search: "",
        position: "All",
        sortKey: "status", // Default sort
        sortOrder: "asc",
        ...initialFilters
    });

    const loadUsers = useCallback(async (isMounted = { current: true }) => {
        setLoading(true);
        setError("");
        try {
            // Determine roleId from position filter using live roles from API
            const roleId = resolveRoleId(filters.position, roles);

            const params = {
                page: pagination.currentPage,
                limit: pagination.limit,
                search: filters.search,
                ...(roleId && { roleId }), // Add roleId if found
                ...(filters.position && filters.position !== "All" && { position: filters.position }) // Add position just in case backend expects it
            };

            const response = await getUsers(params);

            // If the component has unmounted or a newer request has started, ignore this response
            if (!isMounted.current) return;

            let rawUsers = [];
            let total = 0;
            let totalPages = 1;

            if (response.result && Array.isArray(response.result.users)) {
                rawUsers = response.result.users;
                total = response.result.total || rawUsers.length;
                totalPages = response.result.totalPages || 1;
            } else if (Array.isArray(response)) {
                rawUsers = response;
                total = rawUsers.length;
            } else if (response.data) {
                rawUsers = response.data;
                total = response.total || rawUsers.length;
            }

            // Map user structure
            const getPositionLabel = (code) => {
                switch (code) {
                    case "OA": return "OA";
                    case "SRVY": return "SRVY";
                    case "CON": return "CON";
                    default: return code;
                }
            };

            const mappedUsers = rawUsers.map((u) => {
                const roleCode = u.role?.name || u.position || "N/A";
                return {
                    ...u,
                    roleCode,
                    position: getPositionLabel(roleCode),
                    status: (u.status || "active").toLowerCase(),
                };
            });


            setUsers(mappedUsers);
            setPagination(prev => ({ ...prev, total, totalPages }));

        } catch (err) {
            if (isMounted.current) {
                setError(err.message || "Failed to load employees");
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    }, [pagination.currentPage, pagination.limit, filters.search, filters.position, roles]);

    // Actions
    const handleAddUser = async (userData) => {
        try {
            await addEmployee(userData);
            loadUsers(); // Reload to get new data
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    const handleUpdateUser = async (id, data) => {
        try {
            await updateUser(id, data);
            loadUsers();
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    const handleUpdateStatus = async (id, status) => {
        try {
            const next = normalizeEmployeeStatus(status);
            const current = users.find((u) => u.id === id || u._id === id);
            if (
                current &&
                isEmployeeInactiveStatus(current.status) &&
                next === "Active"
            ) {
                toast.error(INACTIVE_TO_ACTIVE_REQUIRES_SUPPORT_MESSAGE);
                loadUsers();
                return { success: false, error: INACTIVE_TO_ACTIVE_REQUIRES_SUPPORT_MESSAGE };
            }

            await updateUserStatus(id, next);
            // Optimistic update — store lowercase to match list display
            setUsers((prev) =>
                prev.map((u) =>
                    u.id === id || u._id === id
                        ? { ...u, status: String(next).toLowerCase() }
                        : u,
                ),
            );
            return { success: true };
        } catch (err) {
            const msg =
                err?.message ||
                err?.response?.message ||
                "Failed to update status";
            toast.error(msg);
            setError(msg);
            loadUsers(); // Revert on failure
            return { success: false, error: msg };
        }
    };

    const handleDeleteUser = async (id) => {
        try {
            await deleteUser(id);
            loadUsers();
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    // Local UI state for table
    const [editing, setEditing] = useState({ id: null, field: "" });

    const handleCellChange = async (id, field, value) => {
        if (field === "status") {
            await handleUpdateStatus(id, value);
        } else {
            await handleUpdateUser(id, { [field]: value });
        }
        setEditing({ id: null, field: "" });
    };

    const toggleSort = (key) => {
        setFilters(prev => ({
            ...prev,
            sortKey: key,
            sortOrder: prev.sortKey === key && prev.sortOrder === "asc" ? "desc" : "asc"
        }));
    };

    // Client-side processing (sorting only, filtering is now server-side)
    const displayedUsers = useMemo(() => {
        const result = [...users];
        if (!filters.sortKey) return result;

        return result.sort((a, b) => {
            let valA = a[filters.sortKey];
            let valB = b[filters.sortKey];

            // Handle null/undefined
            if (valA === null || valA === undefined) valA = "";
            if (valB === null || valB === undefined) valB = "";

            // If both are numbers, compare numerically
            if (typeof valA === 'number' && typeof valB === 'number') {
                return filters.sortOrder === 'asc' ? valA - valB : valB - valA;
            }

            // Handle date strings if they match YYYY-MM-DD
            const isDate = (val) => typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val);
            if (isDate(valA) && isDate(valB)) {
                const dateA = new Date(valA).getTime();
                const dateB = new Date(valB).getTime();
                return filters.sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
            }

            // Fallback to string comparison
            const strA = valA.toString().toLowerCase();
            const strB = valB.toString().toLowerCase();

            if (strA < strB) return filters.sortOrder === "asc" ? -1 : 1;
            if (strA > strB) return filters.sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }, [users, filters.sortKey, filters.sortOrder]);

    const exportData = async (exportFilter, search, currentPosition) => {
        const roleId = resolveRoleId(currentPosition, roles);

        const params = {
            search: search,
            ...(roleId && { roleId }),
            ...(exportFilter.type === "active" ? { status: "Active" } : {}),
            ...(exportFilter.type === "inactive" ? { status: "Inactive" } : {}),
            page: 1,
            limit: 1000
        };
        try {
            const blob = await exportUsersCSV(params);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `employees_export_${new Date().toISOString().split('T')[0]}.csv`;
            a.click();
            return { success: true };
        } catch (e) {
            return { success: false, error: e.message };
        }
    };

    // Load roles once for accurate roleId resolution (DB ids are not fixed)
    useEffect(() => {
        getRoles()
            .then(setRoles)
            .catch(() => setRoles([]));
    }, []);

    // Initial load
    useEffect(() => {
        const isMounted = { current: true };
        loadUsers(isMounted);
        return () => {
            isMounted.current = false;
        };
    }, [loadUsers]);

    return {
        users: displayedUsers, // Return processed users to checking component
        rawUsers: users, // Access to original data if needed
        loading,
        error,
        pagination,
        filters,
        editing,
        setEditing,
        setFilters,
        setPagination,
        loadUsers,
        addUser: handleAddUser,
        updateUser: handleUpdateUser,
        updateStatus: handleUpdateStatus,
        deleteUser: handleDeleteUser,
        handleCellChange,
        toggleSort,
        exportData
    };
};
